import type { MinigolfLabels } from './types'

/**
 * UI strings of the maintenance page, kept next to the localized copy in this
 * folder rather than in the i18n catalogue, matching how the maintenance copy
 * has been handled since it was carried over from the previous static page.
 * One entry per `content/<lang>.html`, English as the fallback.
 *
 * `documentTitle` is the heading the HTML snippets used to carry ("Wartungsarbeiten",
 * "Underhåll", …) and therefore already professionally translated.
 */
export type MaintenanceLabels = {
    /** `<title>` of the page — the headline is too long to double as one. */
    documentTitle: string
    /** One-liner above the game explaining the controls. */
    gameIntro: string
    /** Stroke count of the hole that was just finished, shown below the game. */
    lastResult: (strokes: number) => string
    game: MinigolfLabels
}

/** Plural forms keyed by the categories `Intl.PluralRules` returns for the language. */
type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }

type LangCopy = {
    documentTitle: string
    gameIntro: string
    canvasAria: string
    newHole: string
    nextHole: string
    holedTitle: string
    /** Noun in the status badge, e.g. "Loch" in "Loch 3, 2 Schläge". */
    hole: string
    /** Lead-in of the holed-out status, e.g. "Eingelocht in" in "Eingelocht in 2 Schlägen". */
    holedIn: string
    /** Lead-in below the game, e.g. "Letztes Ergebnis:". */
    lastResult: string
    /** Forms of "stroke" as a plain count. */
    strokes: PluralForms
    /** Forms of "stroke" after `holedIn`, where the language needs another case. */
    strokesAfterIn?: PluralForms
}

const copy: Record<string, LangCopy> = {
    de: {
        documentTitle: 'Wartungsarbeiten',
        gameIntro: 'Ab auf die Runde: Ball anfassen, zurückziehen, loslassen – und abschießen. Jedes Loch wird neu generiert.',
        canvasAria: 'Minigolf-Minispiel: Ball ins Loch schlagen. Ball anfassen, zurückziehen und loslassen zum Schießen.',
        newHole: 'Neues Loch',
        nextHole: 'Nächstes Loch',
        holedTitle: 'Eingelocht!',
        hole: 'Loch',
        holedIn: 'Eingelocht in',
        lastResult: 'Letztes Ergebnis:',
        strokes: { one: 'Schlag', other: 'Schläge' },
        strokesAfterIn: { one: 'Schlag', other: 'Schlägen' }
    },
    en: {
        documentTitle: 'Maintenance',
        gameIntro: 'Up for a round? Grab the ball, pull back and let go to take your shot. Every hole is generated anew.',
        canvasAria: 'Minigolf minigame: get the ball into the hole. Grab the ball, pull back and let go to take your shot.',
        newHole: 'New hole',
        nextHole: 'Next hole',
        holedTitle: 'Holed out!',
        hole: 'Hole',
        holedIn: 'Holed in',
        lastResult: 'Last result:',
        strokes: { one: 'stroke', other: 'strokes' }
    },
    nl: {
        documentTitle: 'Onderhoud',
        gameIntro: 'Zin in een rondje? Pak de bal, trek hem naar achteren en laat los om te slaan. Elke hole wordt opnieuw gegenereerd.',
        canvasAria: 'Minigolf-minigame: sla de bal in de hole. Pak de bal, trek hem naar achteren en laat los om te slaan.',
        newHole: 'Nieuwe hole',
        nextHole: 'Volgende hole',
        holedTitle: 'In de hole!',
        hole: 'Hole',
        holedIn: 'In de hole in',
        lastResult: 'Laatste resultaat:',
        strokes: { one: 'slag', other: 'slagen' }
    },
    es: {
        documentTitle: 'Mantenimiento',
        gameIntro: '¿Le apetece una ronda? Agarre la bola, tire hacia atrás y suéltela para golpear. Cada hoyo se genera de nuevo.',
        canvasAria: 'Minijuego de minigolf: meta la bola en el hoyo. Agarre la bola, tire hacia atrás y suéltela para golpear.',
        newHole: 'Nuevo hoyo',
        nextHole: 'Hoyo siguiente',
        holedTitle: '¡Embocada!',
        hole: 'Hoyo',
        holedIn: 'Embocada en',
        lastResult: 'Último resultado:',
        strokes: { one: 'golpe', other: 'golpes' }
    },
    it: {
        documentTitle: 'Manutenzione',
        gameIntro: 'Voglia di un giro? Afferri la pallina, la tiri indietro e la rilasci per colpire. Ogni buca viene generata di nuovo.',
        canvasAria: 'Minigioco di minigolf: mandi la pallina in buca. Afferri la pallina, la tiri indietro e la rilasci per colpire.',
        newHole: 'Nuova buca',
        nextHole: 'Buca successiva',
        holedTitle: 'In buca!',
        hole: 'Buca',
        holedIn: 'In buca in',
        lastResult: 'Ultimo risultato:',
        strokes: { one: 'colpo', other: 'colpi' }
    },
    fr: {
        documentTitle: 'Maintenance',
        gameIntro: 'Envie d’un parcours ? Attrapez la balle, tirez-la vers l’arrière puis relâchez pour frapper. Chaque trou est généré à nouveau.',
        canvasAria: 'Mini-jeu de minigolf : mettez la balle dans le trou. Attrapez la balle, tirez-la vers l’arrière puis relâchez pour frapper.',
        newHole: 'Nouveau trou',
        nextHole: 'Trou suivant',
        holedTitle: 'Dans le trou !',
        hole: 'Trou',
        holedIn: 'Dans le trou en',
        lastResult: 'Dernier résultat :',
        strokes: { one: 'coup', other: 'coups' }
    },
    da: {
        documentTitle: 'Vedligeholdelse',
        gameIntro: 'Klar til en runde? Tag fat i bolden, træk tilbage og slip for at slå. Hvert hul genereres på ny.',
        canvasAria: 'Minigolf-minispil: slå bolden i hullet. Tag fat i bolden, træk tilbage og slip for at slå.',
        newHole: 'Nyt hul',
        nextHole: 'Næste hul',
        holedTitle: 'I hullet!',
        hole: 'Hul',
        holedIn: 'I hullet på',
        lastResult: 'Seneste resultat:',
        strokes: { one: 'slag', other: 'slag' }
    },
    sv: {
        documentTitle: 'Underhåll',
        gameIntro: 'Sugen på en runda? Ta tag i bollen, dra bakåt och släpp för att slå. Varje hål genereras på nytt.',
        canvasAria: 'Minigolf-minispel: slå bollen i hålet. Ta tag i bollen, dra bakåt och släpp för att slå.',
        newHole: 'Nytt hål',
        nextHole: 'Nästa hål',
        holedTitle: 'I hål!',
        hole: 'Hål',
        holedIn: 'I hål på',
        lastResult: 'Senaste resultat:',
        strokes: { one: 'slag', other: 'slag' }
    },
    nb: {
        documentTitle: 'Vedlikehold',
        gameIntro: 'Klar for en runde? Ta tak i ballen, dra bakover og slipp for å slå. Hvert hull genereres på nytt.',
        canvasAria: 'Minigolf-minispill: slå ballen i hullet. Ta tak i ballen, dra bakover og slipp for å slå.',
        newHole: 'Nytt hull',
        nextHole: 'Neste hull',
        holedTitle: 'I hullet!',
        hole: 'Hull',
        holedIn: 'I hullet på',
        lastResult: 'Siste resultat:',
        strokes: { one: 'slag', other: 'slag' }
    },
    pt: {
        documentTitle: 'Manutenção',
        gameIntro: 'Apetece-lhe uma volta? Agarre a bola, puxe para trás e solte para bater. Cada buraco é gerado de novo.',
        canvasAria: 'Minijogo de minigolfe: meta a bola no buraco. Agarre a bola, puxe para trás e solte para bater.',
        newHole: 'Novo buraco',
        nextHole: 'Buraco seguinte',
        holedTitle: 'No buraco!',
        hole: 'Buraco',
        holedIn: 'No buraco em',
        lastResult: 'Último resultado:',
        strokes: { one: 'tacada', other: 'tacadas' }
    },
    pl: {
        documentTitle: 'Konserwacja',
        gameIntro: 'Ochota na rundkę? Chwyć piłkę, odciągnij ją i puść, aby uderzyć. Każdy dołek jest generowany na nowo.',
        canvasAria: 'Minigra w minigolfa: wbij piłkę do dołka. Chwyć piłkę, odciągnij ją i puść, aby uderzyć.',
        newHole: 'Nowy dołek',
        nextHole: 'Następny dołek',
        holedTitle: 'Wbita!',
        hole: 'Dołek',
        holedIn: 'Wbita w',
        lastResult: 'Ostatni wynik:',
        strokes: { one: 'uderzenie', few: 'uderzenia', many: 'uderzeń', other: 'uderzenia' }
    },
    cs: {
        documentTitle: 'Údržba',
        gameIntro: 'Chuť na kolo? Chyťte míček, zatáhněte ho dozadu a pusťte, aby se odpálil. Každá jamka se generuje znovu.',
        canvasAria: 'Minihra minigolf: dostaňte míček do jamky. Chyťte míček, zatáhněte ho dozadu a pusťte, aby se odpálil.',
        newHole: 'Nová jamka',
        nextHole: 'Další jamka',
        holedTitle: 'V jamce!',
        hole: 'Jamka',
        holedIn: 'V jamce na',
        lastResult: 'Poslední výsledek:',
        strokes: { one: 'úder', few: 'údery', many: 'úderu', other: 'úderů' }
    },
    sk: {
        documentTitle: 'Údržba',
        gameIntro: 'Chuť na kolo? Chyťte loptičku, potiahnite ju dozadu a pustite, aby ste odpálili. Každá jamka sa generuje nanovo.',
        canvasAria: 'Minihra minigolf: dostaňte loptičku do jamky. Chyťte loptičku, potiahnite ju dozadu a pustite, aby ste odpálili.',
        newHole: 'Nová jamka',
        nextHole: 'Ďalšia jamka',
        holedTitle: 'V jamke!',
        hole: 'Jamka',
        holedIn: 'V jamke na',
        lastResult: 'Posledný výsledok:',
        strokes: { one: 'úder', few: 'údery', many: 'úderu', other: 'úderov' }
    },
    sl: {
        documentTitle: 'Vzdrževanje',
        gameIntro: 'Za en krog? Primite žogico, jo povlecite nazaj in spustite za udarec. Vsaka luknja se ustvari na novo.',
        canvasAria: 'Mini igra minigolf: spravite žogico v luknjo. Primite žogico, jo povlecite nazaj in spustite za udarec.',
        newHole: 'Nova luknja',
        nextHole: 'Naslednja luknja',
        holedTitle: 'V luknji!',
        hole: 'Luknja',
        holedIn: 'V luknji v',
        lastResult: 'Zadnji rezultat:',
        strokes: { one: 'udarec', two: 'udarca', few: 'udarci', other: 'udarcev' },
        strokesAfterIn: { one: 'udarcu', two: 'udarcih', few: 'udarcih', other: 'udarcih' }
    },
    et: {
        documentTitle: 'Hooldus',
        gameIntro: 'Isu ringi järele? Haara pallist, tõmba tagasi ja lase lahti, et lüüa. Iga rada luuakse uuesti.',
        canvasAria: 'Minigolfi minimäng: löö pall auku. Haara pallist, tõmba tagasi ja lase lahti, et lüüa.',
        newHole: 'Uus rada',
        nextHole: 'Järgmine rada',
        holedTitle: 'Augus!',
        hole: 'Rada',
        holedIn: 'Sisse',
        lastResult: 'Viimane tulemus:',
        strokes: { one: 'löök', other: 'lööki' },
        strokesAfterIn: { one: 'löögiga', other: 'löögiga' }
    },
    fi: {
        documentTitle: 'Huolto',
        gameIntro: 'Kierros kutsuu? Tartu palloon, vedä taaksepäin ja päästä irti lyödäksesi. Jokainen reikä luodaan uudelleen.',
        canvasAria: 'Minigolf-minipeli: lyö pallo reikään. Tartu palloon, vedä taaksepäin ja päästä irti lyödäksesi.',
        newHole: 'Uusi reikä',
        nextHole: 'Seuraava reikä',
        holedTitle: 'Reiässä!',
        hole: 'Reikä',
        holedIn: 'Reiässä',
        lastResult: 'Viimeisin tulos:',
        strokes: { one: 'lyönti', other: 'lyöntiä' },
        strokesAfterIn: { one: 'lyönnillä', other: 'lyönnillä' }
    },
    lt: {
        documentTitle: 'Priežiūra',
        gameIntro: 'Norite rato? Suimkite kamuoliuką, patraukite atgal ir paleiskite, kad smogtumėte. Kiekviena duobutė generuojama iš naujo.',
        canvasAria: 'Minigolfo minižaidimas: įmuškite kamuoliuką į duobutę. Suimkite kamuoliuką, patraukite atgal ir paleiskite, kad smogtumėte.',
        newHole: 'Nauja duobutė',
        nextHole: 'Kita duobutė',
        holedTitle: 'Įmušta!',
        hole: 'Duobutė',
        holedIn: 'Įmušta per',
        lastResult: 'Paskutinis rezultatas:',
        strokes: { one: 'smūgis', few: 'smūgiai', other: 'smūgių' },
        strokesAfterIn: { one: 'smūgį', few: 'smūgius', other: 'smūgių' }
    },
    lv: {
        documentTitle: 'Apkope',
        gameIntro: 'Vai gribi apli? Satver bumbiņu, pavelc atpakaļ un atlaid, lai sistu. Katrs caurums tiek ģenerēts no jauna.',
        canvasAria: 'Minigolfa minispēle: iesit bumbiņu caurumā. Satver bumbiņu, pavelc atpakaļ un atlaid, lai sistu.',
        newHole: 'Jauns caurums',
        nextHole: 'Nākamais caurums',
        holedTitle: 'Iesists!',
        hole: 'Caurums',
        holedIn: 'Iesists ar',
        lastResult: 'Pēdējais rezultāts:',
        strokes: { zero: 'sitienu', one: 'sitiens', other: 'sitieni' },
        strokesAfterIn: { zero: 'sitieniem', one: 'sitienu', other: 'sitieniem' }
    }
}

function buildLabels(lang: string, c: LangCopy): MaintenanceLabels {
    const rules = new Intl.PluralRules(lang)
    const pick = (forms: PluralForms, n: number) => forms[rules.select(n)] ?? forms.other
    const counted = (n: number) => `${n} ${pick(c.strokes, n)}`
    const countedAfterIn = (n: number) => `${n} ${pick(c.strokesAfterIn ?? c.strokes, n)}`

    return {
        documentTitle: c.documentTitle,
        gameIntro: c.gameIntro,
        lastResult: n => `${c.lastResult} ${counted(n)}`,
        game: {
            status: (hole, strokes) => `${c.hole} ${hole}, ${counted(strokes)}`,
            holedStatus: strokes => `${c.holedIn} ${countedAfterIn(strokes)}`,
            newHole: c.newHole,
            holedTitle: c.holedTitle,
            holedStrokes: counted,
            nextHole: c.nextHole,
            canvasAria: c.canvasAria
        }
    }
}

const cache = new Map<string, MaintenanceLabels>()

export function getMaintenanceLabels(lang: string): MaintenanceLabels {
    const key = copy[lang] ? lang : 'en'
    let labels = cache.get(key)
    if (!labels) {
        labels = buildLabels(key, copy[key]!)
        cache.set(key, labels)
    }
    return labels
}
